import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DownOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Dropdown,
  Empty,
  Input,
  MenuProps,
  Row,
  Space,
  Spin
} from 'antd';
import type { MenuInfo } from 'rc-menu/lib/interface';

import ChangeStatusDropdown from 'components/ChangeStatusDropdown';

import { selectClientId } from 'features/auth/authSelectors';
import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import JobsService from 'features/jobs/jobsService';

import useErrorCatch from 'hooks/useErrorCatch';

import { put } from 'utils/api/api';

import { Filters } from 'types/filters';
import { Job } from 'types/jobs';

import { JOB } from 'constants/endpoints';
import { Dictionaries } from 'constants/enums';
import { PAGE_SIZE } from 'constants/uiSettings';

import { ReactComponent as ArrowIcon } from 'assets/icons/arrowDown.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

import styles from './JobShortPreview.module.scss';

const JobShortPreview: FC = () => {
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const clientId = useSelector(selectClientId);

  const [jobs, setJobs] = useState<Job[]>([]);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const jobStatuses = useSelector(selectDictionary(Dictionaries.JobStatus));
  const searchTerm = useRef('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (clientId) {
      resetJobs();
    }
  }, [clientId, selectedStatus]);

  const resetJobs = () => {
    setPage(0);
    setJobs([]);
    loadJobs(0);
  };

  const loadJobs = async (page: number) => {
    if (!clientId) return;

    setLoading(true);

    const filters: Filters = {
      page,
      pageSize: PAGE_SIZE,
      searchTerm: searchTerm.current,
      status: selectedStatus
    };

    try {
      const res = await JobsService.getJobsByClientId(clientId, filters);

      setHasMore(!res.last);
      setPage(res.pageable.pageNumber);
      setJobs((prevJobs) =>
        page === 0 ? res.content : [...prevJobs, ...res.content]
      );
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.loadJobs')
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const jobsStatusesOptions: MenuProps['items'] = useMemo(() => {
    return jobStatuses.map(({ label, value }) => ({
      key: value,
      label: label
    }));
  }, [jobStatuses]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    searchTerm.current = e.target.value;
    resetJobs();
  };

  const handleSortStatusChange: MenuProps['onClick'] = (info: MenuInfo) => {
    setSelectedStatus(info.key as string);
  };

  const handleSortStatusClear = () => {
    setSelectedStatus('');
  };

  const updateJobStatus = (id: string, newStatus: string) => {
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === id ? { ...job, status: newStatus } : job
      )
    );
  };

  const handleChangeStatus = async (value: string, id: string) => {
    try {
      await put(`${JOB}/${id}/status/${value}`, {});
      updateJobStatus(id, value);
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.loadClientStatus')
        }
      });
    }
  };

  const handleBackView = () => {};

  const lastJobElementRef = (node: HTMLDivElement | null) => {
    if (loading) {
      return;
    }
    if (observer.current) {
      observer.current.disconnect();
    }
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        loadJobs(page + 1);
      }
    });
    if (node) {
      observer.current.observe(node);
    }
  };

  return (
    <>
      <Button
        onClick={handleBackView}
        icon={<ArrowIcon />}
        className={styles.backButton}
      >
        {t('common.back')}
      </Button>
      {/* <SidebarNavigation
        id={clientId}
        data={clients}
        action={setActiveUserId}
      /> */}
      {/* {currentClient?.name && (
        <div className={styles.heading}>{`${currentClient.name} (${
          currentClient.jobsCount || 0
        })`}</div>
      )} */}
      <Input
        value={searchTerm.current}
        name="searchTerm"
        placeholder={t('form.search.placeholder')}
        onChange={handleSearchChange}
        prefix={<SearchIcon />}
        className={styles.searchContainer}
      />
      <div className={styles.statusContainer}>
        <Dropdown
          menu={{
            items: jobsStatusesOptions,
            onClick: handleSortStatusChange,
            selectedKeys: [selectedStatus!]
          }}
        >
          <Button type="link">
            <Space>
              {t('common.label.status')}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
        {selectedStatus && (
          <Button type="link" danger onClick={handleSortStatusClear}>
            {t('common.label.clearSorting')}
          </Button>
        )}
      </div>
      <Row gutter={[16, 16]}>
        {jobs.map((job, index) => {
          const isLastElement = jobs.length === index + 1;
          return (
            <div
              className={styles.jobCardContainer}
              ref={isLastElement ? lastJobElementRef : null}
              key={job.id}
            >
              <Col span={24}>
                <ChangeStatusDropdown
                  value={job.status}
                  onChange={(value) => handleChangeStatus(value, job.id)}
                  dict={Dictionaries.JobStatus}
                />
                <div className={styles.label}>{t('card.job.status')}</div>
              </Col>
              <Col span={24}>
                {job.title}
                <div className={styles.label}>{t('sidebar.job.jobName')}</div>
              </Col>
              <Col span={12}>
                {job.filledOrders || 0}
                <div className={styles.label}>
                  {t('sidebar.job.filledOrders')}
                </div>
              </Col>
              <Col span={12}>
                {job.resumeInReview || 0}
                <div className={styles.label}>
                  {t('sidebar.job.pendingOffers')}
                </div>
              </Col>
            </div>
          );
        })}
      </Row>
      {loading && <Spin className={styles.loadingSpinner} />}
      {jobs.length === 0 && !loading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </>
  );
};

export default JobShortPreview;
